import React, {
  useEffect,
  useRef,
  useState,
  Suspense,
  lazy,
  useMemo,
} from "react";
import {
  Grid,
  Tooltip,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import axios from "axios";
import Layout from "../../../layout/Layout";
import { config, clientMasterAPI, manageStoreMasterAPI } from "../../../config";
import { useNavigate } from "react-router-dom";
import EditLocationOutlinedIcon from "@mui/icons-material/EditLocationOutlined";
import IconButton from "@mui/material/IconButton";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import DataTable from "react-data-table-component";
import { Tooltip as ReactTooltip } from "react-tooltip";
import SearchIcon from "@mui/icons-material/Search";
import { MaterialReactTable } from "material-react-table";

const Pagination = lazy(() => import("../../../components/ReactPagination"));

let field = "id",
  orderBy = "desc";

function StoreMasterList() {
  const navigate = useNavigate();

  let intOffset = 0;
  const limit = 10;
  const [totalCount, setTotalCount] = useState(0);
  const [clientMasterList, setClientMasterList] = useState([]);

  const handleEditStore = (id) => {
    navigate(`/store/edit/${id}`);
  };

  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10); //change to 10 after you remove paginationRowsPerPageOptions
  const [currentPage, setCurrentPage] = useState(1);
  const [searchString, setSearchString] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);

  let tempRefs = useRef(false);
  useEffect(() => {
    if (tempRefs.current === false) {
      fetchUsers(1);
    }
  }, []);

  const fetchUsers = async (
    page,
    rowsPerPage,
    searchString = "",
    columnName = "",
    sortDirection = ""
  ) => {
    const data = JSON.stringify({
      intLimit: pageSize,
      intOffset: pageIndex * pageSize,
      txtSearchString: searchString,
      txtColumnName: columnName,
      txtSortDirection: sortDirection,
      intType: 3,
      sorting,
    });

    config.method = "POST";
    config.data = data;
    config.url = manageStoreMasterAPI;
    axios(config)
      .then(function (response) {
        setClientMasterList(response.data["result"]);

        setTotalCount(response.data.totalCount);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  useEffect(() => {
    fetchUsers();
  }, [pageIndex, pageSize, sorting]);

  const handleSearch = (searchString) => {
    setSearchString(searchString);
    fetchUsers(1, rowsPerPage, searchString);
  };

  const columns = useMemo(
    () => [
      {
        header: "Store Name",
        accessorKey: "store_name",
        size: 200,
      },
      {
        header: "Address",
        accessorKey: "address",
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue()} arrow>
            <span>{cell.getValue()}</span>
          </Tooltip>
        ),
      },
      {
        header: "City",
        accessorKey: "city",
        size: 200,
      },
      {
        header: "State",
        accessorKey: "state",
        size: 120,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue()} arrow>
            <span>{cell.getValue()}</span>
          </Tooltip>
        ),
      },
      {
        header: "PIN Code",
        accessorKey: "pin_code",
        size: 100,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue()} arrow>
            <span>{cell.getValue()}</span>
          </Tooltip>
        ),
      },
      {
        header: "Action",
        accessorKey: "id", // still needed to uniquely identify
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => (
          <IconButton
            color="success"
            onClick={() => handleEditStore(row.original.id)}
          >
            <EditLocationOutlinedIcon />
          </IconButton>
        ),
      },
    ],
    []
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              List Store
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Store</Typography>
              <Typography color="text.primary">List</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <LoadingButton
              size="medium"
              variant="contained"
              color="success"
              className="button_first"
              href="/store/add"
              startIcon={<AddIcon />}
            >
              Add Store
            </LoadingButton>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={3}
          sx={{ mr: 4, mt: 2, justifyContent: `center` }}
        >
          <Grid item xs={12} md={6} lg={4}>
            <TextField
              id="myTextField"
              label="Search"
              placeholder="Search"
              onKeyUp={(e) => handleSearch(e.target.value)}
              variant="outlined"
              margin="normal"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "40px",
                  paddingLeft: "20px", // Adjust the padding if necessary
                },
                "& .MuiInputBase-input": {
                  textAlign: "center", // Center the placeholder text
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <MaterialReactTable
          columns={columns}
          data={clientMasterList}
          enableGlobalFilter={false}
          enableFullScreenToggle={false}
          enableColumnFilters={false}
          enableDensityToggle={false}
          className="mat-react-tbl"
          state={{
            isLoading: loading,
            pagination: { pageIndex, pageSize },
            sorting,
            columnFilters: filters,
          }}
          manualPagination
          manualSorting
          manualFiltering
          rowCount={totalCount}
          onPaginationChange={(updater) => {
            const paginationState = updater({
              pageIndex,
              pageSize,
            });
            const { pageIndex: newPageIndex, pageSize: newPageSize } =
              paginationState;
            setPageIndex(newPageIndex);
            setPageSize(newPageSize);
          }}
          onSortingChange={setSorting}
          onColumnFiltersChange={setFilters}
          muiTableBodyCellProps={{
            sx: {
              padding: "3px 8px",
              border: "1px solid #ccc",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              "& input": {
                color: "#fff !important",
                "&::placeholder": {
                  color: "#fff !important",
                  opacity: 1,
                },
              },
              "& .MuiSelect-select": {
                color: "#fff !important", // selected text color
              },
              "& .MuiSelect-icon": {
                color: "#fff !important", // dropdown arrow color
              },
              color: "#fff",
              fontWeight: "bold",
              "& svg": {
                color: "#fff",
              },
              "& .MuiTableSortLabel-root": {
                color: "#495057",
                "&.Mui-active": {
                  color: "#fff",
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                },
              },
              "& .MuiTableSortLabel-icon": {
                color: "#fff !important",
              },
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            sx: {
              borderBottom: "1px solid #ccc",
              backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
            },
          })}
          muiTableHeadCellFilterTextFieldProps={{
            sx: {
              "& input": {
                color: "#fff !important", // Change text color
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "#fff !important", // Change underline color
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "#fff !important", // Hover underline color
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#fff !important", // Focused underline color
              },
            },
          }}
        />
      </Grid>
    </Layout>
  );
}

export default StoreMasterList;
